/**
 * MessageBubble — Single message in a thread conversation.
 * Shows avatar, author, timestamp, content with rendered @mentions,
 * and file attachment detection.
 */

import React from "react";
import { Avatar, Typography, theme } from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import { renderMentions } from "@/utils/mentions";
import { timeAgo, formatDateTime } from "@/utils/timeAgo";

const { Text } = Typography;

interface MessageBubbleProps {
  content: string;
  authorName: string;
  createdTime: string;
}

/** Detect file attachment pattern: 📎 Allegato: filename (id) */
function isAttachmentMessage(content: string): boolean {
  return content.startsWith("📎 Allegato:");
}

function parseAttachment(content: string): { filename: string; id: string } | null {
  const match = content.match(/📎 Allegato: (.+?) \((\S+)\)$/);
  if (match) return { filename: match[1], id: match[2] };
  return null;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  content,
  authorName,
  createdTime,
}) => {
  const {
    token: { colorBgTextHover, colorTextSecondary, fontSizeSM, borderRadiusLG },
  } = theme.useToken();

  const attachment = isAttachmentMessage(content)
    ? parseAttachment(content)
    : null;

  const initials = authorName
    ? authorName
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        padding: "8px 0",
      }}
    >
      <Avatar
        size={32}
        style={{
          backgroundColor: stringToColor(authorName),
          flexShrink: 0,
          fontSize: 12,
        }}
      >
        {initials}
      </Avatar>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header: name + time */}
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            marginBottom: 2,
          }}
        >
          <Text strong style={{ fontSize: 13 }}>
            {authorName || "—"}
          </Text>
          <Text
            type="secondary"
            style={{ fontSize: fontSizeSM - 1 }}
            title={formatDateTime(createdTime)}
          >
            {timeAgo(createdTime)}
          </Text>
        </div>

        {/* Content */}
        {attachment ? (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              background: colorBgTextHover,
              borderRadius: borderRadiusLG,
              fontSize: 13,
            }}
          >
            <PaperClipOutlined style={{ color: colorTextSecondary }} />
            <span>{attachment.filename}</span>
          </div>
        ) : (
          <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
            {renderMentions(content)}
          </div>
        )}
      </div>
    </div>
  );
};

/** Deterministic color from a string (for avatar backgrounds) */
function stringToColor(str: string): string {
  if (!str) return "#8c8c8c";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    "#6366f1", "#8b5cf6", "#ec4899", "#f43f5e",
    "#f97316", "#eab308", "#22c55e", "#14b8a6",
    "#06b6d4", "#3b82f6", "#a855f7", "#d946ef",
  ];
  return colors[Math.abs(hash) % colors.length];
}
